import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProject } from "@/hooks/useProject";
import { Mail, FileText, Loader2, CheckCircle, AlertCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { getSubmissionName } from "@/utils/submissionHelpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

interface Submission {
  id: number;
  jotform_submission_id: string;
  email: string | null;
  report_url: string | null;
  report_generated_at: string | null;
  raw_answers: any;
}

const BulkSendPage = () => {
  const { projectClient, currentProject } = useProject();
  const queryClient = useQueryClient();
  const [selectedLaunch, setSelectedLaunch] = useState<string>("");
  const [selectedSubmissions, setSelectedSubmissions] = useState<number[]>([]);
  const [processingIds, setProcessingIds] = useState<number[]>([]);
  const [processedIds, setProcessedIds] = useState<number[]>([]);
  const [errorIds, setErrorIds] = useState<number[]>([]);

  // Query para obtener lanzamientos
  const { data: launches, isLoading: isLoadingLaunches } = useQuery({
    queryKey: ["launches-bulk", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("form_launches")
        .select(`
          *,
          clientes (id, nombre),
          jotform_form_mappings:form_mapping_id (id, jotform_form_id, form_title),
          report_templates_v2:template_v2_id (id, name, ppt_file_path)
        `)
        .eq('project_id', projectClient.getProjectId())
        .not('template_v2_id', 'is', null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Query para obtener códigos del lanzamiento seleccionado
  const { data: launchCodes } = useQuery({
    queryKey: ["launch-codes-bulk", selectedLaunch],
    enabled: !!selectedLaunch,
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("form_codes")
        .select("code")
        .eq('form_launch_id', parseInt(selectedLaunch))
        .eq('project_id', projectClient.getProjectId());

      if (error) throw error;
      return data?.map(c => c.code) || [];
    },
  });

  // Query para obtener submissions del lanzamiento seleccionado (filtrado por códigos)
  const { data: submissions, isLoading: isLoadingSubmissions } = useQuery({
    queryKey: ["submissions-bulk", selectedLaunch, launchCodes],
    enabled: !!selectedLaunch && !!launchCodes && launchCodes.length > 0,
    queryFn: async () => {
      const launch = launches?.find(l => l.id.toString() === selectedLaunch);
      if (!launch?.jotform_form_mappings?.jotform_form_id || !launchCodes) return [];

      const { data, error } = await projectClient
        .from("jotform_submissions")
        .select("id, jotform_submission_id, email, report_url, report_generated_at, raw_answers")
        .eq('jotform_form_id', launch.jotform_form_mappings.jotform_form_id)
        .eq('project_id', projectClient.getProjectId())
        .eq('is_referral', false)
        .in('code', launchCodes)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Submission[];
    },
  });

  // Mutation para generar y enviar reporte
  const sendReportMutation = useMutation({
    mutationFn: async ({ submissionId, email }: { submissionId: number; email: string }) => {
      const launch = launches?.find(l => l.id.toString() === selectedLaunch);
      if (!launch) throw new Error("Lanzamiento no encontrado");

      // Buscar submission en la lista
      const submission = submissions?.find(s => s.id === submissionId);
      if (!submission) throw new Error("Submission no encontrada");

      let reportUrl = submission.report_url;

      // Si no tiene reporte, generarlo
      if (!reportUrl) {
        const { data: reportData, error: reportError } = await supabase.functions.invoke('render-report-v3', {
          body: {
            submissionId: submission.jotform_submission_id,
            formId: launch.jotform_form_mappings?.jotform_form_id,
            templateId: launch.template_v2_id,
            clientId: launch.id_cliente,
            launchId: launch.id,
          }
        });

        if (reportError) throw reportError;
        reportUrl = reportData?.reportUrl;

        // Actualizar submission con la URL del reporte
        if (reportUrl) {
          await projectClient
            .from("jotform_submissions")
            .update({
              report_url: reportUrl,
              report_generated_at: new Date().toISOString(),
            })
            .eq('id', submissionId);
        }
      }

      if (!reportUrl) throw new Error("No se pudo generar el reporte");

      // Enviar email con el reporte
      const { error: emailError } = await supabase.functions.invoke('send-report-email', {
        body: {
          to: email,
          reportUrl,
          submissionName: getSubmissionName(submission),
          clientName: launch.clientes?.nombre,
        }
      });

      if (emailError) throw emailError;

      return { submissionId, reportUrl };
    },
    onSuccess: ({ submissionId }) => {
      setProcessedIds(prev => [...prev, submissionId]);
      setProcessingIds(prev => prev.filter(id => id !== submissionId));
      queryClient.invalidateQueries({ queryKey: ["submissions-bulk", selectedLaunch] });
    },
    onError: (error, { submissionId }) => {
      setErrorIds(prev => [...prev, submissionId]);
      setProcessingIds(prev => prev.filter(id => id !== submissionId));
      toast.error(`Error procesando submission ${submissionId}: ${error.message}`);
    },
  });

  // Handler para enviar reportes seleccionados
  const handleBulkSend = async () => {
    const submissionsToSend = submissions?.filter(s =>
      selectedSubmissions.includes(s.id) && s.email
    ) || [];

    if (submissionsToSend.length === 0) {
      toast.error("Selecciona al menos un envío con email válido");
      return;
    }

    setProcessingIds(submissionsToSend.map(s => s.id));
    setProcessedIds([]);
    setErrorIds([]);

    for (const submission of submissionsToSend) {
      if (submission.email) {
        await sendReportMutation.mutateAsync({
          submissionId: submission.id,
          email: submission.email,
        });
      }
    }

    toast.success(`Proceso completado: ${processedIds.length} enviados, ${errorIds.length} errores`);
  };

  // Seleccionar/deseleccionar todos
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubmissions(submissions?.map(s => s.id) || []);
    } else {
      setSelectedSubmissions([]);
    }
  };

  // Seleccionar/deseleccionar uno
  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedSubmissions(prev => [...prev, id]);
    } else {
      setSelectedSubmissions(prev => prev.filter(i => i !== id));
    }
  };

  const selectedLaunchData = launches?.find(l => l.id.toString() === selectedLaunch);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Envío Masivo de Reportes</h1>
      </div>

      {/* Selector de lanzamiento */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">Seleccionar Lanzamiento</label>
            <Select value={selectedLaunch} onValueChange={setSelectedLaunch}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un lanzamiento..." />
              </SelectTrigger>
              <SelectContent>
                {launches?.map(launch => (
                  <SelectItem key={launch.id} value={launch.id.toString()}>
                    {launch.clientes?.nombre} - {launch.jotform_form_mappings?.form_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedLaunchData && (
            <div className="text-sm text-gray-600">
              <p><strong>Plantilla:</strong> {selectedLaunchData.report_templates_v2?.name}</p>
              <p><strong>Cliente:</strong> {selectedLaunchData.clientes?.nombre}</p>
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas */}
      {submissions && submissions.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">{submissions.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Con reporte</p>
            <p className="text-2xl font-bold text-green-600">
              {submissions.filter(s => s.report_url).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Sin reporte</p>
            <p className="text-2xl font-bold text-orange-600">
              {submissions.filter(s => !s.report_url).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Seleccionados</p>
            <p className="text-2xl font-bold text-blue-600">{selectedSubmissions.length}</p>
          </div>
        </div>
      )}

      {/* Tabla de submissions */}
      {selectedLaunch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-medium">Submissions</h2>
            <Button
              onClick={handleBulkSend}
              disabled={selectedSubmissions.length === 0 || processingIds.length > 0}
            >
              <Send className="w-4 h-4 mr-2" />
              Enviar Seleccionados ({selectedSubmissions.length})
            </Button>
          </div>

          {isLoadingSubmissions ? (
            <div className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"><Skeleton className="h-4 w-4" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-12" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-24" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-32" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-20" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-16" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : submissions && submissions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedSubmissions.length === submissions.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reporte</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedSubmissions.includes(submission.id)}
                        onCheckedChange={(checked) => handleSelectOne(submission.id, !!checked)}
                        disabled={processingIds.includes(submission.id)}
                      />
                    </TableCell>
                    <TableCell>{submission.id}</TableCell>
                    <TableCell>{getSubmissionName(submission)}</TableCell>
                    <TableCell>{submission.email || <span className="text-gray-400">Sin email</span>}</TableCell>
                    <TableCell>
                      {submission.report_url ? (
                        <Badge variant="outline" className="bg-green-100 text-green-800">
                          <FileText className="w-3 h-3 mr-1" />
                          Generado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          Pendiente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {processingIds.includes(submission.id) && (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      )}
                      {processedIds.includes(submission.id) && (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                      {errorIds.includes(submission.id) && (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No hay submissions para este lanzamiento
            </div>
          )}
        </div>
      )}

      {!selectedLaunch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Selecciona un lanzamiento para ver los envíos</p>
        </div>
      )}
    </div>
  );
};

export default BulkSendPage;
